import React, { useEffect, useState, Fragment } from "react";
import { useLocation, useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { auth_actions, global_actions, order_actions } from "../../redux-store";
import { Notification, url_encode } from "../../utils";
import backend from "../../api";
import { gaTransaction } from "../Tracking/Tracking";
import { formatPrice } from "../../utils/formatPrice";
import ClipLoader from "react-spinners/ClipLoader";

const OrderSuccess = (props) => {
  const { state: rdr_state } = useLocation();
  const { order_id } = useParams();
  const history = useHistory();

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const allorders = useSelector((state) => state.order.orders);
  const { symbol } = useSelector((state) => state.data);
  const { loading } = useSelector((state) => state.global);

  const [state, setState] = useState(null);
  const [select, setSelect] = useState("");
  const [orderstatus, setOrderstatus] = useState([]);
  const [current, setCurrent] = useState({});
  const [subtotal, setSubtotal] = useState(0);
  const [ordertotal, setOrdertotal] = useState(0);
  const [totalSalesTax, setTotalSalesTax] = useState(0); 

  const getCurrentStatus = (order) => {
    const statuses = Object.keys(order.status).map((key) => ({
      status: key,
      date: order.status[key].date,
    }));
    statuses.sort((a, b) => new Date(b.date) - new Date(a.date));
    return statuses[0];
  };

  const fetchOrder = async () => {
    try {
      dispatch(global_actions.loading(true));
      const { data } = await backend.get("/order", {
        params: {
          _id: order_id,
          populate: url_encode([
            {
              path: "customer",
              populate: [{ path: "user", select: "-password" }],
            },
          ]),
        },
      });
      if (data.data.length === 1) {
        const orderData = data.data[0];
        setState(orderData);
        setCurrent(getCurrentStatus(orderData));
      } else {
        throw new Error("No Such Order Found");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      const status = new Notification();
      status.set_toast(
        "error",
        error.response ? error.response.data.message : error.message
      );
      status.show_toast();
      setTimeout(() => {
        history.replace("/shop");
      }, 500);
    } finally {
      dispatch(global_actions.loading(false));
    }
  };

  useEffect(() => {
    if (rdr_state && rdr_state._id && !order_id) {
      setState(rdr_state);
      setCurrent(getCurrentStatus(rdr_state));
    } else if (!(rdr_state && rdr_state._id) && order_id) {
      if (allorders.length > 0) {
        const order = allorders.find((ao) => ao._id === order_id);
        if (order) {
          setCurrent(getCurrentStatus(order));
          setState(order);
        } else {
          fetchOrder();
        }
      } else {
        fetchOrder();
      }
    } else {
      history.replace("/shop");
    }
  }, [order_id, allorders]);

  useEffect(() => {
    if (state) {
      gaTransaction(
        state.items,
        state.order_track_id,
        state.total_price,
        state.total_sales_tax,
        state.billing_details.delivery_charges
      );

      // Calculate subtotal from product_details
      const subtotalCalc = state?.product_details?.reduce(
        (acc, item, index) => {
          const itemSubtotal = item.product_price * state.items[index].quantity;
          return acc + itemSubtotal;
        },
        0
      );

      setSubtotal(formatPrice(subtotalCalc));

      // Calculate total sales tax from product_details
      const totalTax = state?.product_details?.reduce((acc, item, index) => {
        const itemTax =
          ((item.product_price * item.sales_tax) / 100) *
          state.items[index].quantity;
        return acc + itemTax;
      }, 0);

      setTotalSalesTax(formatPrice(totalTax));

      // Calculate order total
      const orderTotalCalc =
        subtotalCalc +
        parseFloat(state.billing_details.delivery_charges) +
        totalTax;
      setOrdertotal(formatPrice(orderTotalCalc));

      let status_ = Object.keys(state.status).sort(
        (a, b) =>
          Date.parse(state.status[a].date) - Date.parse(state.status[b].date)
      );

      let statuses = status_.map((st) => {
        let status_message, status_title, status_date;
        status_message = state.status[st].message;
        status_date = state.status[st].date;
        switch (st) {
          case "checked_out":
            status_title = "Checked Out";
            break;
          case "declined":
            status_title = "Declined";
            break;
          case "in_progress":
            status_title = "In Progress";
            break;
          case "delivered":
            status_title = "Delivered";
            break;
          case "cancel":
            status_title = "Cancelled";
            break;
          case "return":
            status_title = "Returned";
            break;
          default:
            break;
        }
        return { status_title, status_message, status_date };
      });

      setOrderstatus(statuses);
    }
  }, [state]);

  const setSelectState = async (e) => {
    const newStatus = e.target.value;
    setSelect(newStatus);

    try {
      const data = {
        order_id: order_id,
        date: new Date().toISOString(),
        status: newStatus,
      };

      await dispatch(order_actions.update_order_status(data));
      fetchOrder();
    } catch (error) {
      console.error("Error updating order:", error);
      const status = new Notification();
      status.set_toast(
        "error",
        error.response ? error.response.data.message : error.message
      );
      status.show_toast();
    }
  };

  const getAvailableStatuses = (currentStatus) => {
    const statusOrder = [
      "checked_out",
      "in_progress",
      "delivered",
      // "cancel",
      // "declined",
      // "return",
    ];
    const currentIndex = statusOrder.indexOf(currentStatus);
    return statusOrder.slice(currentIndex + 1);
  };

  const renderOrderStatus = () => {
    return orderstatus.map((ord) => (
      <Fragment key={ord.status_title}>
        <li>
          {ord.status_title} &mdash; {new Date(ord.status_date).toDateString()}
        </li>
        <li>{new Date(ord.status_date).toLocaleTimeString()}</li>
        <li className="mb-3">{ord.status_message}</li>
      </Fragment>
    ));
  };

  return (
    <Fragment>
      {loading ? ( 
        <div className="d-flex justify-content-center my-5">
          <ClipLoader color={"#000000"} loading={true} size={50} />
        </div>
      ) : (
        state &&
        current &&
        user &&
        orderstatus.length > 0 &&
        subtotal &&
        ordertotal && (
          <div>
            <section className="section-b-space light-layout">
              <div className="container">
                <div className="row">
                  <div className="col-md-12">
                    <div className="success-text">
                      <i className="fa fa-check-circle" aria-hidden="true" />
                      {!order_id ? (
                        <Fragment>
                          <h2>Thank You</h2>
                          <p>
                            Payment has been received and order placed
                            successfully.
                          </p>
                        </Fragment>
                      ) : (
                        <Fragment>
                          <h2>
                            Order Status &mdash;{" "}
                            <small>
                              {current.status.replace("_", " ").toUpperCase()}
                            </small>
                          </h2>
                          <p>{current.status_message}</p>
                          {user?.user?.role === "merchant" &&
                            getAvailableStatuses(current.status).length > 0 && (
                              <div className="my-4">
                                <select
                                  className="form-control d-inline w-auto"
                                  id="change_status"
                                  name="status"
                                  value={select}
                                  onChange={setSelectState}
                                >
                                  <option value="">Change Status</option>
                                  {getAvailableStatuses(current.status).map(
                                    (status) => (
                                      <option key={status} value={status}>
                                        {status.replace("_", " ").toUpperCase()}
                                      </option>
                                    )
                                  )}
                                </select>
                              </div>
                            )}
                        </Fragment>
                      )}
                      <p>
                        Order ID &mdash; <b>{state.order_track_id}</b>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="section-b-space">
              <div className="container">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="product-order">
                      <h3>Your Order Details</h3>
                      {state?.product_details?.map((item, index) => (
                        <div className="row product-order-detail" key={index}>
                          <div className="col-3">
                            <img
                              src={item?.product_images?.[0]?.image}
                              alt=""
                              className="img-fluid"
                            />
                          </div>
                          <div className="col-3 order_detail">
                            <div>
                              <h4>Product Name</h4>
                              <h5>{item.product_name}</h5>
                            </div>
                          </div>
                          <hr />
                          <div className="col-3 order_detail">
                            <div>
                              <h4>Quantity</h4>
                              <h5>{state.items[index].quantity}</h5>
                            </div>
                          </div>
                          <div className="col-3 order_detail">
                            <div>
                              <h4>Price</h4>
                              <h5>
  {formatPrice(
    (state.items[index].quantity * item.product_price)
  )}
</h5>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="total-sec">
                        <ul>
                          <li>
                            Subtotal{" "}
                            <span>
                              {subtotal}
                            </span>
                          </li>
                          <li>
                            Delivery Charges{" "}
                            <span>
                              {formatPrice(state.billing_details.delivery_charges)}
                            </span>
                          </li>
                          <li>
                            Sales tax(GST){" "}
                            <span>
                              {totalSalesTax}
                            </span>
                          </li>
                          {user?.user?.role === "merchant" && (
                            <ul>
                              <li>
                                Author Charges{" "}
                                <span>
                                  {formatPrice(state.author_amount)}
                                </span>
                              </li>
                              <li>
                                Member Charges{" "}
                                <span>
                                  {formatPrice(state.member_amount)}
                                </span>
                              </li>
                              <li>
                                Eksplode Charges{" "}
                                <span>
                                  {formatPrice(state.owner_amount)}
                                </span>
                              </li>
                              <li>
                                My Amount{" "}
                                <span>
                                  {formatPrice(state.merchant_amount)}
                                </span>
                              </li>
                            </ul>
                          )}
                        </ul>
                      </div>
                      <div className="final-total">
                        <h3>
                          Total{" "}
                          <span>
                            {ordertotal}
                          </span>
                        </h3>
                      </div>
                      <hr />
                    </div>
                  </div>
                </div>
                <div className="row mt-5 order-success-sec justify-content-center">
                  <div className="col-md-12 py-3">
                    <h4>Summary</h4>
                    <ul className="order-detail">
                      {renderOrderStatus()}
                      <li>
                        Order Total:
                        {ordertotal}
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-12 py-3">
                    <h4> Shipping Address</h4>
                    <ul className="order-detail">
                      <li>{state.address.name}</li>
                      <li>{state.address.city}</li>
                      <li>
                        {state.address.state} - {state.address.zip}
                      </li>
                      <li>{state.address.country}</li>
                    </ul>
                  </div>
                  {user.role === "merchant" && (
                    <div className="col-md-12 py-3">
                      <h4> Customer Details</h4>
                      <ul className="order-detail">
                        <li>
                          <strong> Name:</strong>{" "}
                          {state.customer.user.first_name +
                            " " +
                            state.customer.user.last_name}
                        </li>
                        <li>
                          <strong>Email:</strong> {state.customer.user.email}{" "}
                        </li>
                        <li>
                          <strong>Address:</strong>{" "}
                          {state.address.name +
                            " " +
                            state.address.city +
                            ", " +
                            state.address.country +
                            "."}
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              </section>
          </div>
        )
      )}
    </Fragment>
  );
};

export default OrderSuccess;
